import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { InlineMessageRenderer } from './InlineMessageRenderer'
import InlineMessageErrorBoundary from './InlineMessageErrorBoundary'
import StandaloneComponentRenderer from './StandaloneComponentRenderer'
import useInlineActions from '../../hooks/chat/useInlineActions'
import { ErrorMessage } from './ErrorMessage'

/**
 * ChatMessageWithParts component - Renders messages using the new parts-based structure.
 *
 * The parts system stores only response/error types in the parts array, while status/progress
 * messages are display-only and not persisted. This component computes the display content
 * from the parts array and handles streaming states appropriately.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.message - Message object with parts structure
 * @param {Array} props.message.parts - Array of response/error parts (only persisted types)
 * @param {boolean} props.message.isProgress - Whether currently showing progress/status
 * @param {string} props.message.currentStatus - Current status message (display-only)
 * @param {string} props.message.statusType - Type of current status ('status' or 'progress')
 * @param {boolean} props.isStreaming - Whether message is currently streaming
 * @param {boolean} props.isLastMessage - Whether this is the last message
 * @param {Function} props.onSentenceClick - Callback for clickable sentence interactions
 * @param {string} props.assistantAnimationState - Animation state for the assistant
 *
 * @returns {JSX.Element} Rendered message with parts-based content
 */
export const ChatMessageWithParts = ({
  message,
  isStreaming,
  isLastMessage,
  onSentenceClick,
  assistantAnimationState,
}) => {
  const { handleComponentAction } = useInlineActions()
  const isUser = message.role === 'user'
  const isNewUserMessage = isUser && isLastMessage && !isStreaming
  const isUserMessageBeingProcessed = isUser && isLastMessage && isStreaming
  const showStreamingIndicator = message.role === 'assistant' && isStreaming && isLastMessage

  // Compute display content from parts array
  const displayData = useMemo(() => {
    const parts = message.parts || []

    // Separate text content from component content
    const textParts = parts.filter((part) => !part.isComponent && part.type === 'response')
    const componentParts = parts.filter((part) => part.isComponent && part.type === 'response')
    const errorParts = parts.filter((part) => part.type === 'error')

    // During streaming, combine stored parts + current status for display
    if (isStreaming && isLastMessage && message.isProgress && message.currentStatus) {
      // Concatenate all stored text-only response parts (components handled separately)
      const storedTextContent = textParts.map((part) => part.message || '').join('')

      return {
        content: storedTextContent + message.currentStatus,
        isProgress: true,
        isError: message.isError || errorParts.length > 0,
        statusType: message.statusType,
        hasStoredContent: storedTextContent.length > 0,
        // Check if any parts have interactive features
        hasComponents: componentParts.length > 0,
        // Extract metadata for rendering
        components: parts.find((part) => part.components)?.components || {},
        componentType: parts.find((part) => part.componentType)?.componentType,
        allParts: parts,
        textParts,
        componentParts,
        errorParts,
      }
    } else {
      // Show only stored parts (response/error types), separating text from components
      const textContent = textParts.map((part) => part.message || '').join('')
      const hasErrors = errorParts.length > 0

      return {
        content: textContent,
        isProgress: false,
        isError: hasErrors,
        statusType: null,
        hasStoredContent: textContent.length > 0,
        // Check if any parts have interactive features
        hasComponents: componentParts.length > 0,
        // Extract metadata for rendering
        components: parts.find((part) => part.components)?.components || {},
        componentType: parts.find((part) => part.componentType)?.componentType,
        allParts: parts,
        textParts,
        componentParts,
        errorParts,
      }
    }
  }, [
    message.parts,
    message.isProgress,
    message.currentStatus,
    message.statusType,
    message.isError,
    isStreaming,
    isLastMessage,
  ])

  const getTextClasses = () =>
    `leading-relaxed whitespace-pre-wrap ${
      displayData.isProgress ? 'text-sm text-gray-500 italic' : 'text-sm sm:text-base'
    }`

  const getAnimationClasses = () => {
    // Only apply animations to assistant messages
    if (message.role !== 'assistant') return ''

    // Only apply animations to the last assistant message
    if (!isLastMessage) return ''

    // Apply animations based on assistantAnimationState prop
    switch (assistantAnimationState) {
      case 'thinking':
        return 'animate-status-thinking'
      case 'processing':
        return 'animate-progress-working'
      case 'streaming':
        return 'animate-response-typing'
      case 'complete':
        return 'animate-response-complete'
      case 'idle':
      default:
        return ''
    }
  }

  /**
   * Renders a component part based on its type and data.
   * Handles both parsed component markers and complex component objects.
   */
  const renderComponentPart = (part, partKey) => {
    if (part.componentType === 'heading') {
      // Handle heading components - use parsed data or extract from marker
      const headingText = part.displayText || part.title || part.message || 'Heading'
      const headingLevel = part.level || 3
      const clampedLevel = Math.min(Math.max(headingLevel, 1), 6) // Clamp between 1-6

      // Use conditional JSX rendering instead of createElement for better React compatibility
      switch (clampedLevel) {
        case 1:
          return (
            <h1 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h1>
          )
        case 2:
          return (
            <h2 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h2>
          )
        case 3:
          return (
            <h3 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h3>
          )
        case 4:
          return (
            <h4 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h4>
          )
        case 5:
          return (
            <h5 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h5>
          )
        case 6:
          return (
            <h6 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h6>
          )
        default:
          return (
            <h3 key={partKey} className="font-semibold text-gray-200 mt-4 mb-2 first:mt-0">
              {headingText}
            </h3>
          )
      }
    } else if (part.componentType === 'button') {
      // Handle button components
      const buttonText = part.displayText || part.text || 'Button'
      const buttonAction = part.action || ''

      return (
        <div key={partKey} className="my-2">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
            onClick={() =>
              handleComponentAction({ type: 'button', action: buttonAction, text: buttonText })
            }
          >
            {buttonText}
          </button>
        </div>
      )
    } else if (part.componentType === 'link') {
      // Handle link components
      const linkText = part.displayText || part.text || 'Link'
      const linkUrl = part.url || '#'

      return (
        <div key={partKey} className="my-2">
          <a
            href={linkUrl}
            className="text-blue-400 hover:text-blue-300 underline transition-colors"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleComponentAction({ type: 'link', url: linkUrl, text: linkText })}
          >
            {linkText}
          </a>
        </div>
      )
    } else if (part.componentType === 'badge') {
      // Handle badge components
      const badgeText = part.displayText || part.text || 'Badge'
      const badgeVariant = part.variant || 'default'
      const badgeClasses = {
        default: 'bg-gray-600 text-gray-200',
        success: 'bg-green-600 text-green-100',
        warning: 'bg-yellow-600 text-yellow-100',
        error: 'bg-red-600 text-red-100',
        info: 'bg-blue-600 text-blue-100',
      }

      return (
        <div key={partKey} className="my-2">
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded ${badgeClasses[badgeVariant] || badgeClasses.default}`}
          >
            {badgeText}
          </span>
        </div>
      )
    } else if (part.componentType === 'code') {
      // Handle code components
      const codeContent = part.displayText || part.code || part.message || ''
      const codeLanguage = part.language || 'text'

      return (
        <div key={partKey} className="my-2">
          <div className="bg-gray-800 rounded p-3 text-sm">
            <div className="text-gray-400 text-xs mb-1">{codeLanguage}</div>
            <pre className="overflow-x-auto">
              <code className="text-gray-200">{codeContent}</code>
            </pre>
          </div>
        </div>
      )
    } else if (part.components) {
      // Handle complex components from standalone renderer
      return (
        <div key={partKey} className="my-2">
          <InlineMessageErrorBoundary message={message}>
            <StandaloneComponentRenderer
              components={part.components}
              layout={part.layout}
              onComponentAction={handleComponentAction}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    } else {
      // Handle unknown component types - render as simple component
      const componentData = {
        type: part.componentType,
        text: part.displayText || part.message || '',
        ...part.metadata,
        ...part.componentData,
      }

      return (
        <div key={partKey} className="my-2">
          <InlineMessageErrorBoundary message={message}>
            <StandaloneComponentRenderer
              component={componentData}
              onComponentAction={handleComponentAction}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    }
  }

  const renderContent = () => {
    // Handle error messages with special styling
    if (displayData.isError && displayData.errorParts?.length > 0) {
      const errorPart = displayData.errorParts[0] // Show first error
      const errorContent = errorPart.message || ''

      return (
        <ErrorMessage
          message={errorContent}
          trace={errorPart.trace}
          type={errorPart.isWarning ? 'warning' : 'error'}
        />
      )
    }

    // Check for standalone single component from parts
    const singleComponentPart = displayData.allParts.find(
      (part) =>
        part.type === 'response' && part.isComponent && part.componentType && !part.components
    )
    if (singleComponentPart && displayData.allParts.length === 1) {
      // Use renderComponentPart to properly handle the component
      const partKey = `${message.message_id}-single-component`
      return renderComponentPart(singleComponentPart, partKey)
    }

    // Check for standalone multiple components from parts
    const multiComponentPart = displayData.allParts.find(
      (part) => part.type === 'response' && part.isComponent && Array.isArray(part.components)
    )
    if (multiComponentPart) {
      return (
        <InlineMessageErrorBoundary message={message}>
          <StandaloneComponentRenderer
            components={multiComponentPart.components}
            layout={multiComponentPart.layout}
            onComponentAction={handleComponentAction}
          />
        </InlineMessageErrorBoundary>
      )
    }

    // Check for response with components (message + components)
    const hasResponseWithComponents = displayData.allParts.some(
      (part) =>
        part.type === 'response' && part.components && Object.keys(part.components).length > 0
    )
    if (hasResponseWithComponents) {
      const componentPart = displayData.allParts.find(
        (part) => part.type === 'response' && part.components
      )

      return (
        <div>
          {displayData.content && (
            <div className={`${getTextClasses()} mb-3`}>
              {displayData.content}
              {showStreamingIndicator && !componentPart.components && (
                <span className="inline-block w-1 h-4 ml-1 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
              )}
            </div>
          )}
          <InlineMessageErrorBoundary message={message}>
            <StandaloneComponentRenderer
              components={componentPart.components}
              layout={componentPart.layout}
              onComponentAction={handleComponentAction}
            />
          </InlineMessageErrorBoundary>
          {showStreamingIndicator && !displayData.content && (
            <div className="mt-2">
              <span className="inline-block w-1 h-4 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
            </div>
          )}
        </div>
      )
    }

    // Handle multi-part messages with both text and components in correct order
    if (displayData.componentParts.length > 0 || displayData.textParts.length > 0) {
      // Render component parts separately, text content as markdown
      const hasComponents = displayData.componentParts.length > 0

      return (
        <div>
          {displayData.content && (
            <div className={`${getTextClasses()} prose prose-invert prose-sm max-w-none`}>
              <ReactMarkdown>{displayData.content}</ReactMarkdown>
            </div>
          )}
          {hasComponents &&
            displayData.componentParts
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((part, index) => {
                const partKey = `${message.message_id}-component-${part.order || index}`
                return renderComponentPart(part, partKey)
              })}
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 ml-1 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
        </div>
      )
    }

    // Check for inline components (message with [[componentId]] markers or [[type:param:...]] inline data)
    // Only use this fallback if we don't have structured parts
    if (
      displayData.content &&
      displayData.content.includes('[[') &&
      displayData.allParts.length === 0
    ) {
      const mockMessage = {
        type: 'response',
        content: displayData.content,
        message: displayData.content,
        components: displayData.components || {},
        sentence_id: displayData.allParts.find((part) => part.sentence_id)?.sentence_id || '',
        message_id: message.message_id,
        conversation_id: message.conversation_id,
        hasClickableSentences: false,
        sentences: [],
        messageId: message.message_id,
        conversationId: message.conversation_id,
      }

      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <InlineMessageErrorBoundary message={message}>
            <InlineMessageRenderer
              message={mockMessage}
              onComponentAction={handleComponentAction}
              onSentenceClick={onSentenceClick}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    }

    // Check for response messages with components object (from parts) - only as fallback
    if (
      displayData.hasComponents &&
      Object.keys(displayData.components).length > 0 &&
      displayData.allParts.length === 0
    ) {
      const mockMessage = {
        type: 'response',
        content: displayData.content,
        message: displayData.content,
        components: displayData.components || {},
        sentence_id: '',
        message_id: message.message_id,
        conversation_id: message.conversation_id,
        hasClickableSentences: false,
        sentences: [],
        messageId: message.message_id,
        conversationId: message.conversation_id,
      }

      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <InlineMessageErrorBoundary message={message}>
            <InlineMessageRenderer
              message={mockMessage}
              onComponentAction={handleComponentAction}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    }

    // Fallback to markdown rendering
    return (
      <div className={`${getTextClasses()} prose prose-invert prose-sm max-w-none`}>
        {showStreamingIndicator && (
          <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
        )}
        <ReactMarkdown>{displayData.content}</ReactMarkdown>
      </div>
    )
  }

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${
        isNewUserMessage
          ? 'animate-message-send'
          : isUser || !isStreaming || !isLastMessage
            ? 'animate-fade-in'
            : ''
      }`}
      data-conversation-id={message.conversation_id || ''}
      data-message-id={message.message_id || ''}
    >
      <div className="max-w-[85%] sm:max-w-[75%]">
        <div
          className={`relative ${displayData.isError ? '' : 'px-5 py-4'} ${
            isUser
              ? 'message-bubble-user-modern'
              : displayData.isError
                ? 'bg-transparent border-transparent'
                : 'message-bubble-assistant-modern'
          } transition-all duration-200 hover:shadow-lg ${
            isUserMessageBeingProcessed ? 'animate-message-pulse' : getAnimationClasses()
          }`}
        >
          {renderContent()}

          {!displayData.isError && (
            <div
              className={`mt-2 flex items-center gap-2 text-xs ${
                isUser ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <span className="opacity-75">
                {new Date().toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}

          {/* Debug info for development */}
          {process.env.NODE_ENV === 'development' && message.parts && (
            <details className="mt-2 text-xs opacity-50">
              <summary>Parts Debug ({message.parts.length} parts)</summary>
              <pre className="mt-1 text-xs overflow-x-auto">
                {JSON.stringify(
                  {
                    parts: message.parts,
                    isProgress: message.isProgress,
                    currentStatus: message.currentStatus,
                    statusType: message.statusType,
                    displayData: {
                      content:
                        displayData.content?.substring(0, 100) +
                        (displayData.content?.length > 100 ? '...' : ''),
                      hasComponents: displayData.hasComponents,
                      componentParts: displayData.componentParts?.length || 0,
                      textParts: displayData.textParts?.length || 0,
                    },
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  )
}

ChatMessageWithParts.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.oneOf(['user', 'assistant']).isRequired,
    message_id: PropTypes.string.isRequired,
    conversation_id: PropTypes.string.isRequired,
    parts: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['response', 'error']).isRequired,
        message: PropTypes.string,
        timestamp: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
        sentence_id: PropTypes.string, // For clickable sentences - each part with sentence_id becomes a clickable sentence
        isComponent: PropTypes.bool,
        componentType: PropTypes.string,
        components: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        layout: PropTypes.string,
        trace: PropTypes.string, // For error parts
        isWarning: PropTypes.bool, // For warning errors
        metadata: PropTypes.object,
      })
    ),
    // State flags (not persisted in storage)
    isProgress: PropTypes.bool,
    isError: PropTypes.bool,
    currentStatus: PropTypes.string, // Current status message (display-only)
    statusType: PropTypes.oneOf(['status', 'progress']), // Type of current status
    // Analytics metadata
    timestamp: PropTypes.string,
    lastUpdated: PropTypes.string,
    streamingDuration: PropTypes.number,
  }).isRequired,
  isStreaming: PropTypes.bool,
  isLastMessage: PropTypes.bool,
  onSentenceClick: PropTypes.func,
  assistantAnimationState: PropTypes.oneOf([
    'idle',
    'thinking',
    'processing',
    'streaming',
    'complete',
  ]),
}

ChatMessageWithParts.defaultProps = {
  isStreaming: false,
  isLastMessage: false,
  onSentenceClick: undefined,
  assistantAnimationState: 'idle',
}
